import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Download, Sparkles, Users, Heart, TrendingUp, Zap, Instagram, Twitter, Linkedin, ChevronDown } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import Header from '@/components/Header';
import '@/App.css';

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  // Use a single scroll subscription to avoid duplicate listeners / work on every scroll frame
  const { scrollY, scrollYProgress } = useScroll();
  
  // Parallax effects with smooth interpolation
  const y1 = useTransform(scrollY, [0, 1000], [0, -200], { clamp: false });
  const y2 = useTransform(scrollY, [0, 1000], [0, -100], { clamp: false });
  const opacity = useTransform(scrollY, [0, 300], [1, 0], { clamp: true });
  
  // Smooth spring animation for scroll progress
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 150,
    damping: 40,
    restDelta: 0.001,
    mass: 0.5
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Particles configuration
  const particlesInit = useCallback(async engine => {
    await loadSlim(engine);
  }, []);

  const particlesOptions = useMemo(() => ({
    fullScreen: { enable: false },
    background: { color: { value: 'transparent' } },
    // Keep the canvas lighter so scroll stays responsive on lower-end devices
    fpsLimit: 30,
    particles: {
      number: { value: 35, density: { enable: true, area: 900 } },
      color: { value: ['#097CE4', '#CD0093', '#5eb3f6'] },
      shape: { type: 'circle' },
      opacity: {
        value: 0.3,
        random: true,
        animation: { enable: true, speed: 0.5, minimumValue: 0.1 }
      },
      size: {
        value: { min: 1, max: 4 },
        random: true,
        animation: { enable: true, speed: 2, minimumValue: 0.5 }
      },
      links: {
        enable: true,
        distance: 150,
        color: '#097CE4',
        opacity: 0.2,
        width: 1
      },
      move: {
        enable: true,
        speed: 1,
        direction: 'none',
        random: true,
        straight: false,
        outModes: { default: 'out' }
      }
    },
    interactivity: {
      detectsOn: 'canvas',
      events: {
        onHover: { enable: true, mode: 'grab' },
        resize: true
      },
      modes: {
        grab: { distance: 140, links: { opacity: 0.5 } }
      }
    },
    detectRetina: true
  }), []);

  // Animation variants for scroll reveals
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  // Intersection observers for scroll animations
  const [problemRef, problemInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [trustRef, trustInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [statsRef, statsInView] = useInView({ threshold: 0.5, triggerOnce: true });

  return (
    <div className="app-container" data-testid="landing-page">
      {/* Scroll Progress Bar */}
      <motion.div 
        className="scroll-progress"
        style={{ scaleX }}
      />

      {/* Hero Section */}
      <section className="hero-section" id="hero" data-testid="hero-section">
        <Header />
        {/* Animated Background Layers */}
        <div className="animated-bg-container">
          <div className="animated-bg-layer layer-1"></div>
          <div className="animated-bg-layer layer-2"></div>
          <div className="animated-bg-layer layer-3"></div>
        </div>
        
        {/* Particles Background */}
        <div className="particles-container">
          <Particles
            id="tsparticles"
            init={particlesInit}
            options={particlesOptions}
          />
        </div>
        
        <div className="hero-overlay"></div>
        <motion.div 
          className="hero-content"
          style={{ touchAction: 'pan-y' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: isVisible ? 1 : 0 }}
          transition={{ duration: 1 }}
        >
          <motion.div 
            className="hero-text"
            style={{ y: y1 }}
          >
            <motion.div 
              className="cyber-badge" 
              data-testid="cyber-badge"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, duration: 0.8, type: 'spring' }}
            >
              <Sparkles className="w-4 h-4" />
              <span>THE FUTURE OF FASHION</span>
            </motion.div>
            
            <motion.h1 
              className="hero-title" 
              data-testid="hero-title"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Don't Just View It.
              <br />
              <motion.span 
                className="gradient-text"
                initial={{ backgroundPosition: '0% 50%' }}
                animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
                style={{ backgroundSize: '200% 200%' }}
              >
                Wear It.
              </motion.span>
            </motion.h1>
            
            <motion.p 
              className="hero-subtitle" 
              data-testid="hero-subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              The first social app where you can virtually try on any outfit you see in your feed. 
              Shop with confidence.
            </motion.p>
            
            <motion.div 
              className="cta-buttons" 
              data-testid="cta-buttons"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              <motion.a
                href="https://play.google.com/store/apps/details?id=com.gotoecomapp"
                target="_blank"
                rel="noopener noreferrer"
                className="store-button playstore"
                data-testid="playstore-button"
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(139, 92, 246, 0.6)' }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="store-button-text">
                  <div className="store-button-label">GET IT ON</div>
                  <div className="store-button-name">Google Play</div>
                </div>
              </motion.a>
              <motion.a
                href="https://apps.apple.com/in/app/gotoecom/id6743615043"
                target="_blank"
                rel="noopener noreferrer"
                className="store-button appstore"
                data-testid="appstore-button"
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(236, 72, 153, 0.5)' }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                </svg>
                <div className="store-button-text">
                  <div className="store-button-label">DOWNLOAD ON</div>
                  <div className="store-button-name">App Store</div>
                </div>
              </motion.a>
            </motion.div>
          </motion.div>

          <motion.div 
            className="hero-visual"
            style={{ y: y2, touchAction: 'pan-y' }}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            data-testid="hero-visual"
          >
            <motion.div 
              className="split-screen"
              style={{ touchAction: 'pan-y' }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div className="split-left">
                <motion.div 
                  className="image-wrapper"
                  style={{ touchAction: 'pan-y' }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.4 }}
                >
                  <img 
                    src="https://images.unsplash.com/photo-1648692792913-9510b0b39cea" 
                    alt="Fashion Creator Outfit"
                    className="split-image"
                    draggable="false"
                  />
                  <div className="image-label">CREATOR'S STYLE</div>
                </motion.div>
              </div>
              <motion.div 
                className="split-divider"
                style={{ touchAction: 'pan-y' }}
                animate={{ 
                  rotate: [0, 360],
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
              >
                <Zap className="w-8 h-8 text-blue-400" />
              </motion.div>
              <div className="split-right">
                <motion.div 
                  className="image-wrapper"
                  style={{ touchAction: 'pan-y' }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.4 }}
                >
                  <img 
                    src="https://app.gotoecom.com/website/virtual_try_on.jpg" 
                    alt="Virtual Try-On"
                    className="split-image"
                    loading="eager"
                    draggable="false"
                    onError={(e) => {
                      console.error('Failed to load image:', e.target.src);
                    }}
                  />
                  <div className="image-label">TRY IT ON</div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div 
          className="scroll-indicator"
          style={{ opacity, x: "-50%" }}
          onClick={() => scrollToSection('problem')}
          data-testid="scroll-indicator"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-6 h-6" />
        </motion.div>
      </section>

      {/* Problem/Solution Section */}
      <section className="section-container" id="problem" data-testid="problem-solution-section" ref={problemRef}>
        <motion.div
          initial="hidden"
          animate={problemInView ? 'visible' : 'hidden'}
          variants={fadeInUp}
        >
          <motion.h2 
            className="section-title" 
            data-testid="section-title-missing-link"
            variants={fadeInUp}
          >
            The <span className="gradient-text">Missing Link</span> in Shopping
          </motion.h2>
          
          <div className="cards-grid" data-testid="cards-grid">
            <motion.div 
              className="feature-card"
              data-testid="card-for-shoppers"
              variants={fadeInLeft}
              whileHover={{ 
                y: -15, 
                boxShadow: '0 25px 70px rgba(139, 92, 246, 0.4)',
                transition: { duration: 0.3 }
              }}
            >
              <motion.div 
                className="card-icon"
                whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                transition={{ duration: 0.5 }}
              >
                <Heart className="w-8 h-8" />
              </motion.div>
              <h3 className="card-title">For Shoppers</h3>
              <p className="card-subtitle">End Purchase Anxiety</p>
              <p className="card-description">
                No more "Will this fit me?" uncertainty. See it on your own Digital Twin before you buy. 
                Make confident decisions every time.
              </p>
              <div className="cyber-line"></div>
            </motion.div>

            <motion.div 
              className="feature-card"
              data-testid="card-for-creators"
              variants={fadeInRight}
              whileHover={{ 
                y: -15, 
                boxShadow: '0 25px 70px rgba(236, 72, 153, 0.4)',
                transition: { duration: 0.3 }
              }}
            >
              <motion.div 
                className="card-icon"
                whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                transition={{ duration: 0.5 }}
              >
                <TrendingUp className="w-8 h-8" />
              </motion.div>
              <h3 className="card-title">For Creators</h3>
              <p className="card-subtitle">Zero-Friction Monetization</p>
              <p className="card-description">
                Stop burying links in bios. Make your style instantly shoppable and try-on-able. 
                Turn influence into income effortlessly.
              </p>
              <div className="cyber-line"></div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="section-container dark-section" id="features" data-testid="features-section" ref={featuresRef}>
        <motion.div
          initial="hidden"
          animate={featuresInView ? 'visible' : 'hidden'}
        >
          <motion.h2 
            className="section-title" 
            data-testid="section-title-core"
            variants={fadeInUp}
          >
            The <span className="gradient-text">Core Experience</span>
          </motion.h2>

          <div className="features-list" data-testid="features-list">
            <motion.div 
              className="feature-row"
              data-testid="feature-show-off"
              variants={fadeInLeft}
            >
              <motion.div 
                className="feature-image-container"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1758520388212-04b4d36e1364" 
                  alt="Show Off Feed"
                  className="feature-image"
                />
                <div className="image-overlay"></div>
              </motion.div>
              <motion.div 
                className="feature-content"
                variants={fadeInRight}
              >
                <div className="feature-number">01</div>
                <h3 className="feature-title">Show Off</h3>
                <p className="feature-subtitle">The Feed</p>
                <p className="feature-description">
                  Discover trends from top creators. Every post is instantly shoppable. 
                  Tap to explore, save to wishlist, or try it on yourself. Fashion inspiration meets instant action.
                </p>
                
                {/* Animated Phone Mockup */}
                <motion.div 
                  className="phone-mockup"
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                >
                  <div className="phone-frame">
                    <div className="phone-screen">
                      <div className="feed-scroll">
                        <motion.div 
                          className="feed-item"
                          animate={{ y: [-300, 0] }}
                          transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
                        >
                          <img src="https://images.unsplash.com/photo-1539533018447-63fcce2678e3" alt="Feed 1" />
                          <div className="feed-info">
                            <div className="feed-badge">🔥 Trending</div>
                          </div>
                        </motion.div>
                        <motion.div 
                          className="feed-item"
                          animate={{ y: [-300, 0] }}
                          transition={{ duration: 15, repeat: Infinity, ease: 'linear', delay: 2.5 }}
                        >
                          <img src="https://images.unsplash.com/photo-1581044777550-4cfa60707c03" alt="Feed 2" />
                          <div className="feed-info">
                            <div className="feed-badge">💜 New Drop</div>
                          </div>
                        </motion.div>
                        <motion.div 
                          className="feed-item"
                          animate={{ y: [-300, 0] }}
                          transition={{ duration: 15, repeat: Infinity, ease: 'linear', delay: 5 }}
                        >
                          <img src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f" alt="Feed 3" />
                          <div className="feed-info">
                            <div className="feed-badge">✨ Must Have</div>
                          </div>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>

            <motion.div 
              className="feature-row reverse"
              data-testid="feature-fitcheck"
              variants={fadeInRight}
            >
              <motion.div 
                className="feature-image-container vto-demo"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              >
                {/* VTO GIF Demo */}
                <div className="vto-container">
                  <motion.div 
                    className="vto-before"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 4, repeat: Infinity }}
                  >
                    <img 
                      src="https://images.unsplash.com/photo-1670804317345-f8602cf7dab2" 
                      alt="Before VTO"
                      className="feature-image"
                    />
                    <div className="vto-label">BEFORE</div>
                  </motion.div>
                  <motion.div 
                    className="vto-after"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 4, repeat: Infinity, delay: 2 }}
                  >
                    <img 
                      src="https://images.unsplash.com/photo-1534528741775-53994a69daeb" 
                      alt="After VTO"
                      className="feature-image"
                    />
                    <div className="vto-label gradient-text">AFTER VTO</div>
                  </motion.div>
                  <motion.div 
                    className="vto-loading"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  >
                    <Sparkles className="w-8 h-8" />
                  </motion.div>
                </div>
                <div className="image-overlay"></div>
              </motion.div>
              <motion.div 
                className="feature-content"
                variants={fadeInLeft}
              >
                <div className="feature-number">02</div>
                <h3 className="feature-title">FitCheck</h3>
                <p className="feature-subtitle">The Utility</p>
                <p className="feature-description">
                  The world's most advanced Virtual Try-On. Upload your photo once, try on infinite outfits. 
                  See exactly how you'll look before making a purchase. No guesswork, just confidence.
                </p>
              </motion.div>
            </motion.div>

            <motion.div 
              className="feature-row"
              data-testid="feature-hub"
              variants={fadeInLeft}
            >
              <motion.div 
                className="feature-image-container"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1613915617430-8ab0fd7c6baf" 
                  alt="The Hub Profile"
                  className="feature-image"
                />
                <div className="image-overlay"></div>
              </motion.div>
              <motion.div 
                className="feature-content"
                variants={fadeInRight}
              >
                <div className="feature-number">03</div>
                <h3 className="feature-title">The Hub</h3>
                <p className="feature-subtitle">The Profile</p>
                <p className="feature-description">
                  Manage your Digital Twins and curate your ultimate wishlist. 
                  Track your style evolution, organize your fits, and keep all your fashion inspiration in one place.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Trust Section */}
      <section className="section-container" id="trust" data-testid="trust-section" ref={trustRef}>
        <motion.div
          className="trust-container"
          initial="hidden"
          animate={trustInView ? 'visible' : 'hidden'}
        >
          <motion.div 
            className="trust-content"
            variants={fadeInLeft}
          >
            <h2 className="section-title" data-testid="section-title-community">
              Powered by <span className="gradient-text">Community Opinion</span>
            </h2>
            <p className="trust-description">
              Not sure about your fit? Post a "Cop or Drop" poll and get real-time votes from the community. 
              Validate your style with authentic feedback from fashion lovers like you.
            </p>
            <div className="trust-stats" ref={statsRef}>
              <motion.div 
                className="stat-item" 
                data-testid="stat-creators"
                variants={scaleIn}
                whileHover={{ x: 15, transition: { duration: 0.2 } }}
              >
                <Users className="w-6 h-6 text-blue-400" />
                <div>
                  <div className="stat-number">
                    {statsInView && <CountUp end={10} duration={2} suffix="K+" />}
                  </div>
                  <div className="stat-label">Active Creators</div>
                </div>
              </motion.div>
              <motion.div 
                className="stat-item" 
                data-testid="stat-tryons"
                variants={scaleIn}
                transition={{ delay: 0.1 }}
                whileHover={{ x: 15, transition: { duration: 0.2 } }}
              >
                <Zap className="w-6 h-6 text-pink-400" />
                <div>
                  <div className="stat-number">
                    {statsInView && <CountUp end={100} duration={2} suffix="K+" />}
                  </div>
                  <div className="stat-label">Virtual Try-Ons</div>
                </div>
              </motion.div>
              <motion.div 
                className="stat-item" 
                data-testid="stat-votes"
                variants={scaleIn}
                transition={{ delay: 0.2 }}
                whileHover={{ x: 15, transition: { duration: 0.2 } }}
              >
                <Heart className="w-6 h-6 text-blue-400" />
                <div>
                  <div className="stat-number">
                    {statsInView && <CountUp end={1} duration={2} suffix="M+" />}
                  </div>
                  <div className="stat-label">Community Votes</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
          <motion.div 
            className="trust-image-container"
            variants={fadeInRight}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.4 }}
          >
            <img 
              src="https://images.unsplash.com/photo-1758520388099-e9bcb137c7d7" 
              alt="Community Shopping"
              className="trust-image"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Footer CTA */}
      <footer className="footer-section" data-testid="footer-section">
        <motion.div
          className="footer-content"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <motion.h2 
            className="footer-title" 
            data-testid="footer-title"
            variants={fadeInUp}
          >
            Your closet is about to get a <span className="gradient-text">major upgrade.</span>
          </motion.h2>
          
          <motion.div 
            className="footer-cta" 
            data-testid="footer-cta"
            variants={fadeInUp}
            transition={{ delay: 0.2 }}
          >
            <motion.a
              href="https://play.google.com/store/apps/details?id=com.gotoecomapp"
              target="_blank"
              rel="noopener noreferrer"
              className="store-button playstore large"
              data-testid="footer-playstore-button"
              whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(139, 92, 246, 0.6)' }}
              whileTap={{ scale: 0.95 }}
            >
              <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div className="store-button-text">
                <div className="store-button-label">GET IT ON</div>
                <div className="store-button-name">Google Play</div>
              </div>
            </motion.a>
            <motion.a
              href="https://apps.apple.com/in/app/gotoecom/id6743615043"
              target="_blank"
              rel="noopener noreferrer"
              className="store-button appstore large"
              data-testid="footer-appstore-button"
              whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(236, 72, 153, 0.5)' }}
              whileTap={{ scale: 0.95 }}
            >
              <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
              </svg>
              <div className="store-button-text">
                <div className="store-button-label">DOWNLOAD ON</div>
                <div className="store-button-name">App Store</div>
              </div>
            </motion.a>
          </motion.div>

          <motion.div 
            className="social-links" 
            data-testid="social-links"
            variants={fadeInUp}
            transition={{ delay: 0.3 }}
          >
            <motion.a 
              href="https://instagram.com/gotoecom" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="social-link" 
              data-testid="instagram-link"
              whileHover={{ y: -5, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Instagram className="w-6 h-6" />
            </motion.a>
            <motion.a 
              href="https://twitter.com/gotoecom" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="social-link" 
              data-testid="twitter-link"
              whileHover={{ y: -5, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Twitter className="w-6 h-6" />
            </motion.a>
            <motion.a 
              href="https://linkedin.com/company/gotoecom" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="social-link" 
              data-testid="linkedin-link"
              whileHover={{ y: -5, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Linkedin className="w-6 h-6" />
            </motion.a>
          </motion.div>

          <motion.div 
            className="footer-bottom"
            variants={fadeInUp}
            transition={{ delay: 0.4 }}
          >
            <p>© 2025 Gotoecom. All rights reserved.</p>
            <div className="footer-links">
              <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
              <span className="footer-link-separator">•</span>
              <Link to="/terms-and-conditions" className="footer-link">Terms and Conditions</Link>
            </div>
          </motion.div>
        </motion.div>
      </footer>
    </div>
  );
};

export default LandingPage;

